import { useMediaQuery, useTheme } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { SaveIcon } from 'assets/icons';
import { useSaveProject } from 'pages/Remixer/hooks';
import useMessageSnackbar from 'services/hooks/useMessageSnackbar';
import { analytics, events } from 'services/analytics';
import { AnalyticsEventLocation } from 'services/analytics/entities';
import { useAuthStore } from 'services/hooks';
import {
  checkCurrentSongLoading,
  checkProjectHasChanges,
} from 'services/store/remixer/selectors';
import { locations } from 'services/routes/locations';
import { StyledLoadingButton } from './styled';

const SaveProjectButton = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { save, isLoading } = useSaveProject();
  const { displaySuccessSnackbar, displayErrorSnackbar } = useMessageSnackbar();

  const isCurrentSongLoading = useSelector(checkCurrentSongLoading);
  const hasChanges = useSelector(checkProjectHasChanges);

  const { authStore } = useAuthStore();
  const { isLoggedIn } = authStore;
  const location = AnalyticsEventLocation.STUDIO;

  const isDisabled = isLoading || !hasChanges || isCurrentSongLoading;

  const handleClick = useCallback(async () => {
    analytics.track(events.studioProjectSave(location));

    if (!isLoggedIn) {
      navigate(locations.general.register.pathname(''));
      return;
    }

    try {
      await save();
      displaySuccessSnackbar(
        t('remixer:snackbarMessages:saveProjectSuccess'),
        t('songList:goToLibrary'),
        () => navigate(locations.library.projects.pathname())
      );
    } catch {
      displayErrorSnackbar(t('remixer:snackbarMessages:saveProjectError'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, save, t, isLoggedIn]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <StyledLoadingButton
      disabled={isDisabled}
      loading={isLoading}
      startIcon={<SaveIcon />}
      onClick={handleClick}
      size="large"
      variant={isMobile ? 'text' : 'outlined'}
    >
      {!isMobile && t('remixer:header:save')}
    </StyledLoadingButton>
  );
};

export default SaveProjectButton;
